import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRightIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { TenantBrandMark } from '../components/common/TenantBrandMark';
import { TenantFooter } from '../components/common/TenantFooter';
import { TenantBackdrop } from '../components/common/TenantBackdrop';
import { useTenant } from '../contexts/TenantContext';
import { getBrand } from '../config/api';
import { loginDestination } from '../utils/loginDestination';

function loginErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  // FastAPI validation errors are objects; only their message belongs in the UI.
  const messages = (Array.isArray(detail) ? detail : [detail]).map(item => {
    if (typeof item === 'string') return item.trim();
    if (item && typeof item === 'object') {
      const record = item as { msg?: unknown; message?: unknown };
      const message = record.msg || record.message;
      if (typeof message === 'string') return message.trim();
    }
    return '';
  }).filter(Boolean);
  return messages.join(' ') || fallback;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedNext = new URLSearchParams(location.search).get('next');
  const destination = loginDestination(requestedNext, getBrand());
  const { login, googleLogin } = useAuth();
  const { tenant } = useTenant();
  const hasTenantCover = Boolean(tenant?.cover_image_url);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Update document class when theme changes
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(destination);
    } catch (err) {
      setError(loginErrorMessage(err, 'Failed to login. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setError('');
    setLoading(true);

    try {
      await googleLogin(credentialResponse.credential);
      navigate(destination);
    } catch (err) {
      // Surface the backend's detail (e.g. tenant-membership 403:
      // "You are not a member of JSPT.") so the user knows whether
      // to retry or contact their admin. Fall back to a generic
      // message only when no detail is present.
      setError(loginErrorMessage(err, 'Failed to login with Google. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden ${
      isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'
    }`}>
      <TenantBackdrop isDarkMode={isDarkMode} />

      {/* Vidya gradient orbs — platform / no-cover-image fallback only.
          When a tenant cover is rendered, TenantBackdrop owns the visuals. */}
      {isDarkMode && !hasTenantCover && (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/10 via-blue-900/10 to-slate-900/10"></div>
          {/* Floating orbs */}
          <div
            className="absolute top-1/4 left-1/4 w-[300px] h-[300px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 60%)',
              filter: 'blur(60px)',
            }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] rounded-full opacity-15"
            style={{
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 60%)',
              filter: 'blur(60px)',
            }}
          />
        </div>
      )}

      {/* Theme toggle button */}
      <button
        onClick={toggleTheme}
        className={`absolute top-8 right-8 p-2 rounded-lg transition-colors ${
          isDarkMode
            ? 'text-slate-400 hover:text-white hover:bg-slate-800'
            : 'text-gray-600 hover:text-gray-900 hover:bg-white'
        }`}
        aria-label="Toggle theme"
      >
        {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
      </button>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <Link to="/" className="block">
          <TenantBrandMark logoSize={44} isDarkMode={isDarkMode} />
        </Link>
        <h2 className={`mt-6 text-center text-2xl font-bold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {tenant ? `Sign in to your ${tenant.name} account` : 'Sign in to your account'}
        </h2>
        <p className={`mt-2 text-center text-sm ${
          isDarkMode ? 'text-slate-400' : 'text-gray-600'
        }`}>
          Or{' '}
          <Link to={destination === '/dashboard' ? '/signup' : `/signup?next=${encodeURIComponent(destination)}`} className={`font-medium transition-colors ${
            isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
          }`}>
            create a new account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className={`py-8 px-4 shadow-xl rounded-2xl sm:px-10 ${
          isDarkMode 
            ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-xl' 
            : 'bg-white border border-gray-200'
        }`}
        style={isDarkMode ? {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
        } : {}}
        >
          {error && (
            <div role="alert" className={`mb-4 px-4 py-3 rounded-lg text-sm ${
              isDarkMode
                ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                : 'bg-red-50 border border-red-200 text-red-600'
            }`}>
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.boxShadow = '0 0 50px rgba(6, 182, 212, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span className="flex items-center">
                  {loading ? 'Signing in...' : 'Sign in'}
                  {!loading && <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                </span>
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${
                  isDarkMode ? 'bg-slate-900/50 text-slate-400' : 'bg-white text-gray-500'
                }`}>
                  {tenant ? `Or continue to ${tenant.name} with` : 'Or continue with'}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google login failed')}
                useOneTap
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 z-10">
        <TenantFooter isDarkMode={isDarkMode} className="border-0" />
      </div>
    </div>
  );
};

export default Login;
